<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'

const emit = defineEmits<{
  (e: 'close'): void
}>()

const { t } = useI18n()

const countdown = ref(3)
let intervalId: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  intervalId = setInterval(() => {
    if (countdown.value > 0) {
      countdown.value -= 1
    }
    if (countdown.value <= 0 && intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
  }, 1000)
})

onBeforeUnmount(() => {
  if (intervalId) {
    clearInterval(intervalId)
    intervalId = null
  }
})

const canClose = computed(() => countdown.value <= 0)

const buttonLabel = computed(() =>
  canClose.value
    ? t('demo.credentialsSent.close')
    : t('demo.credentialsSent.closeIn', { seconds: countdown.value })
)

const handleClose = () => {
  if (!canClose.value) return
  emit('close')
}
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-[300] flex items-center justify-center p-4"
      style="background: rgba(15, 23, 42, 0.45); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);"
    >
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div class="p-6 border-b border-gray-200">
          <h2 class="text-lg font-semibold text-gray-900">{{ t('demo.credentialsSent.title') }}</h2>
        </div>

        <div class="p-6 space-y-6">
          <p class="text-sm text-gray-700 leading-relaxed">
            {{ t('demo.credentialsSent.message') }}
          </p>

          <button
            type="button"
            @click="handleClose"
            :disabled="!canClose"
            class="w-full py-2.5 px-4 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ buttonLabel }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
